import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Tag, Info, Sparkles, MessageCircle } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/lib/contact";
import { CHECKOUT_URLS } from "@/lib/checkout";

function SmartImage({ name, alt, className = "" }) {
  const [src, setSrc] = useState(`/images/${name}.jpg`);
  const [fb, setFb] = useState(false);
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full object-cover ${className}`}
      onError={() => {
        if (!fb) {
          setSrc(`/images/${name}.svg`);
          setFb(true);
        }
      }}
    />
  );
}

function CustomVisual({ label }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-brand-cloud via-brand-paper to-brand-champagneSoft/40 flex flex-col items-center justify-center text-center p-6 grain">
      <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-ink text-brand-champagne">
        <Sparkles className="h-6 w-6" />
      </div>
      <div className="mt-5 font-heading text-base font-medium text-brand-ink tracking-tight">
        {label}
      </div>
      <div className="mt-1 text-xs tracking-[0.22em] uppercase font-semibold text-brand-stone">
        QR · NFC · Logo · Farbe
      </div>
    </div>
  );
}

export default function Bestseller() {
  const { t, lang } = useLang();
  const b = t.bestseller;

  const autoplay = useRef(
    Autoplay({ delay: 3500, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1 },
    [autoplay.current]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      setScrollSnaps(emblaApi.scrollSnapList());
      onSelect();
    });
    onSelect();
  }, [emblaApi]);

  const scrollTo = (i) => emblaApi && emblaApi.scrollTo(i);
  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  const handleInquire = (variant) => {
    try {
      window.sessionStorage.setItem("in7s_inquiry_type", "general");
      window.sessionStorage.setItem("in7s_variant", variant);
      window.dispatchEvent(
        new CustomEvent("in7s:set-variant", { detail: variant })
      );
    } catch (_) {}
  };

  const waUrl = (variant) => {
    const msg =
      lang === "de"
        ? `Hallo in7sekunden, ich interessiere mich für: ${variant}.`
        : `Hi in7sekunden, I'm interested in: ${variant}.`;
    return CONTACT.whatsappUrl(msg);
  };

  return (
    <section id="bestseller" className="section-y bg-white" data-testid="bestseller-section">
      <div className="container-x">
        <div className="max-w-3xl">
          <span className="overline">{b.overline}</span>
          <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
            {b.title}
          </h2>
          <p className="mt-6 text-base md:text-lg text-brand-graphite leading-relaxed text-pretty">
            {b.body}
          </p>
        </div>

        <div
          className="mt-10 rounded-2xl border border-brand-champagne/70 bg-brand-champagneSoft/25 p-5 md:p-6 flex items-start gap-4 max-w-3xl"
          data-testid="bestseller-notice"
          role="note"
        >
          <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-ink text-brand-champagne shrink-0">
            <Info className="h-4 w-4" />
          </span>
          <div>
            <div className="font-heading text-sm md:text-base font-semibold text-brand-ink tracking-tight">
              {b.noticeTitle}
            </div>
            <p className="mt-1 text-sm text-brand-graphite leading-relaxed">
              {b.noticeBody}
            </p>
          </div>
        </div>

        {/* Carousel */}
        <div className="mt-14 relative" data-testid="bestseller-carousel">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex -ml-5 md:-ml-6">
              {b.items.map((item, i) => (
                <div
                  key={i}
                  className="pl-5 md:pl-6 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
                >
                  <article
                    className="card-soft card-soft-hover overflow-hidden flex flex-col h-full"
                    data-testid={`bestseller-${i}`}
                  >
                    <div className="relative aspect-[4/5] bg-brand-cloud overflow-hidden">
                      {item.custom ? (
                        <CustomVisual label={item.title} />
                      ) : (
                        <SmartImage name={item.image} alt={item.title} />
                      )}
                      <div className="absolute top-4 left-4 chip">
                        <Tag className="h-3 w-3" />
                        {item.tag}
                      </div>
                      {item.popular && (
                        <div
                          className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-brand-champagne px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] shadow-sm"
                          data-testid={`bestseller-popular-${i}`}
                        >
                          <Sparkles className="h-3 w-3" />
                          {b.popularBadge}
                        </div>
                      )}
                    </div>
                    <div className="p-6 md:p-7 flex flex-col flex-1">
                      <h3 className="font-heading text-lg md:text-xl font-medium text-brand-ink tracking-tight">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-brand-graphite leading-relaxed flex-1">
                        {item.body}
                      </p>
                      <div className="mt-5 flex items-center justify-between gap-3">
                        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                          <span className="text-xs text-brand-stone uppercase tracking-wider font-semibold">
                            {b.priceLabel}
                          </span>
                          <span className="font-heading text-2xl font-semibold text-brand-ink">
                            {item.price}
                          </span>
                        </div>
                        {CHECKOUT_URLS[item.slug] ? (
                          <a
                            href={CHECKOUT_URLS[item.slug]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary py-2.5 px-4 text-[13px]"
                            data-testid={`bestseller-cta-${i}`}
                          >
                            {b.cta}
                            <ArrowRight className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <a
                            href="/#contact"
                            onClick={() => handleInquire(item.inquiryValue)}
                            className="btn-primary py-2.5 px-4 text-[13px]"
                            data-testid={`bestseller-cta-${i}`}
                          >
                            {b.ctaCustom || b.cta}
                            <ArrowRight className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                      {item.slug && (
                        <div className="mt-4 flex items-center justify-between gap-3">
                          <Link
                            to={`/aufsteller/${item.slug}`}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-brand-stone hover:text-brand-ink transition-colors"
                            data-testid={`bestseller-details-${i}`}
                          >
                            {b.detailsCta}
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                          <a
                            href={waUrl(item.title)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-brand-stone hover:text-brand-ink transition-colors"
                            data-testid={`bestseller-whatsapp-${i}`}
                            aria-label={lang === "de" ? "Per WhatsApp anfragen" : "Ask via WhatsApp"}
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            WhatsApp
                          </a>
                        </div>
                      )}
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="mt-8 flex items-center justify-between gap-6">
            <div className="flex items-center gap-2" data-testid="bestseller-dots">
              {scrollSnaps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollTo(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === selectedIndex
                      ? "w-8 bg-brand-ink"
                      : "w-3 bg-brand-stone/40 hover:bg-brand-stone"
                  }`}
                  data-testid={`bestseller-dot-${i}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={scrollPrev}
                aria-label={lang === "de" ? "Vorheriger" : "Previous"}
                className="h-10 w-10 grid place-items-center rounded-full border border-brand-stone/30 text-brand-ink hover:bg-brand-ink hover:text-white hover:border-brand-ink transition-colors"
                data-testid="bestseller-prev"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={scrollNext}
                aria-label={lang === "de" ? "Nächster" : "Next"}
                className="h-10 w-10 grid place-items-center rounded-full border border-brand-stone/30 text-brand-ink hover:bg-brand-ink hover:text-white hover:border-brand-ink transition-colors"
                data-testid="bestseller-next"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <p className="mt-8 text-xs text-brand-stone text-center md:text-left tracking-wide leading-relaxed max-w-2xl">
          {b.priceNote}
        </p>
      </div>
    </section>
  );
}
